import Avatar from '@/components/elements/Avatar';
import Icon from '@/components/icons/Icon';
import type { IconVariant } from '@/components/icons/types';
import { classnames } from '@/helpers/general';
import { customTwMerge } from '@/helpers/theme';
import { _t } from '@/locale';
import type { KeyboardEvent, ReactNode } from 'react';

const baseKey = 'features.bookingSelection.SelectEmployeeCard';

// The avatar follows the large-button radius token (Figma st/cornerradius/cornerbtnlarge),
// so the widget's Rounded mode turns it into a circle along with the buttons.
// The legacy Avatar only exposes its radius through `style`, and passing `style`
// replaces its whole default style object — so we re-declare the layout props here too.
const avatarStyle = {
  display: 'flex',
  overflow: 'hidden',
  flexShrink: 0,
  alignItems: 'center',
  justifyContent: 'center',
  borderRadius: 'var(--radius-btn-lg)',
} as const;

/** The single-select `rightSlot` indicator, shared by the employee list and panel. */
export const radioSlot = (selected: boolean) => (
  <Icon
    variant={selected ? 'radio-checked' : 'radio-unchecked'}
    color={selected ? 'fg_brand' : 'fg_secondary'}
    size="sm"
  />
);

type SelectEmployeeCardProps = {
  /** Employee display name, e.g. "Johanna J". */
  name: string;
  /** Role/title shown under the name, e.g. "Senior Stylist". */
  jobTitle?: string;
  /** Avatar image URL; falls back to the generic person placeholder when absent. */
  avatarUrl?: string | null;
  /**
   * Renders an icon tile where the avatar would go — the any-employee row's `users-alt`
   * glyph (Figma node 444-48059). Takes precedence over `avatarUrl`.
   */
  avatarIcon?: IconVariant;
  /** Star rating; omit to hide the rating row entirely. */
  rating?: { score: number };
  /** Renders the "Selected" dark border. Independent of whatever icon is in `rightSlot`. */
  selected?: boolean;
  /**
   * The far-right indicator. Purely visual — the parent supplies any `<Icon>` and it
   * reflects whatever `onClick` does. Standard usages:
   *   radio:        <Icon variant={selected ? 'radio-checked' : 'radio-unchecked'} color={selected ? 'fg_brand' : 'fg_secondary'} size="sm" />
   *   navigate:     <Icon variant="chevron-right" color="fg_secondary" size="sm" />
   *   expand:       <Icon variant="chevron-down" color="fg_primary" size="sm" />
   */
  rightSlot?: ReactNode;
  /** Fires when the card is clicked anywhere (except the info button). Omit for a non-interactive card. */
  onClick?: () => void;
  /** Fires when the info (i) button is clicked; the click never bubbles to `onClick`. Omit to hide the info button. */
  onInfoClick?: () => void;
  className?: string;
};

/**
 * A selectable employee card: avatar + name + rating/role, with an optional info (i)
 * button and a caller-supplied right-side indicator. The whole card is one click target;
 * the info button is isolated via stopPropagation. Presentational only — no data or state.
 */
const SelectEmployeeCard = ({
  name,
  jobTitle,
  avatarUrl,
  avatarIcon,
  rating,
  selected = false,
  rightSlot,
  onClick,
  onInfoClick,
  className,
}: SelectEmployeeCardProps) => {
  const isInteractive = Boolean(onClick);

  const handleKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    // Only act on keys targeting the card itself, not inner focusables (the info button).
    if (e.target !== e.currentTarget) return;
    // `keydown` auto-repeats while a key is held; a native button would not re-fire.
    if (e.repeat) return;
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onClick?.();
    }
  };

  return (
    <div
      data-cy="select-employee-card"
      {...(isInteractive ? { role: 'button', tabIndex: 0, onClick, onKeyDown: handleKeyDown } : {})}
      className={customTwMerge(
        classnames(
          'bg-surface_l2 py-sm pl-sm pr-xs gap-sm flex w-full items-center rounded-lg border',
          selected ? 'border-border_selected' : 'drop-shadow-xs border-transparent',
          isInteractive &&
            'focus-visible:outline-border_focus cursor-pointer outline-none focus-visible:outline focus-visible:outline-4',
          isInteractive && !selected && 'hover:border-border_bold hover:drop-shadow-md',
        ),
        className,
      )}>
      {/* Decorative: the name is already announced. Avatar hardcodes alt="noImage" and
          forwards no aria props, so hide the whole subtree from assistive tech. */}
      <span aria-hidden="true" className="contents">
        {avatarIcon ? (
          // 48px tile (h-12 w-12) around a 24px glyph (size="sm"), bg_secondary_bold =
          // #E6EDF2 = Figma's CT/Background/BG Secondary, per node 444-48059. Note
          // `bg_secondary` is #F2F7FA here — the page background, not this tile.
          <span className="bg-bg_secondary_bold rounded-btn-lg flex h-12 w-12 shrink-0 items-center justify-center">
            <Icon variant={avatarIcon} color="fg_secondary" size="sm" />
          </span>
        ) : (
          <Avatar src={avatarUrl ?? undefined} size="md" style={avatarStyle} />
        )}
      </span>

      <div className="gap-xs flex min-w-0 flex-1 items-center">
        <div className="gap-2xs flex min-w-0 flex-1 flex-col">
          <p className="text-l text-fg_primary truncate">{name}</p>
          <div className="gap-xs flex items-center">
            {rating && (
              <span className="gap-2xs flex shrink-0 items-center">
                <Icon variant="star-filled" color="warning-500" size="xs" />
                <span className="text-m text-fg_primary font-bold">{rating.score.toFixed(1)}</span>
              </span>
            )}
            {jobTitle && <span className="text-s text-fg_secondary truncate">{jobTitle}</span>}
          </div>
        </div>

        {(onInfoClick || rightSlot) && (
          <div className="gap-xs flex shrink-0 items-center">
            {onInfoClick && (
              <button
                type="button"
                aria-label={_t(`${baseKey}.infoLabel`)}
                onClick={(e) => {
                  e.stopPropagation();
                  onInfoClick();
                }}
                className="focus-visible:outline-border_focus flex shrink-0 items-center rounded-sm outline-none focus-visible:outline focus-visible:outline-4">
                <Icon variant="info-circle" color="fg_link" size="sm" />
              </button>
            )}
            {rightSlot && <div className="flex shrink-0 items-center">{rightSlot}</div>}
          </div>
        )}
      </div>
    </div>
  );
};

export default SelectEmployeeCard;
